import React, { useEffect, useState } from 'react';
import { Alert, CircularProgress, Divider, IconButton, Tooltip } from '@mui/material';
import styled from '@emotion/styled';
import { useAppDispatch } from '@/redux/hooks';
import axios from '@/_utils/axios';
import type { InventoryFileEntity } from '@/redux/slices/slice_types';
import { setInventoryModalPreviewedFile } from '@/redux/slices/packagingSlice';
import PolarisIcon from '@/components/_common/PolarisIcon';
import InventoryPreview from '@/components/_common/InventoryPreview/InventoryPreview';

interface Props {
  titleId: number;
  previewedFile: InventoryFileEntity & { proxyId?: number };
}

const PreviewPanel: React.FC<Props> = ({ titleId, previewedFile }) => {
  const dispatch = useAppDispatch();
  const [failedToFetchPreviewUrl, setFailedToFetchPreviewUrl] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();

  useEffect(() => {
    const fileId = previewedFile.proxyId ?? previewedFile.id;

    setPreviewUrl(undefined);
    setFailedToFetchPreviewUrl(false);
    axios
      .get(`/inventory/titles/${titleId}/files/${fileId}/`)
      .then((res) => {
        setPreviewUrl(res.data?.data?.url);
        setFailedToFetchPreviewUrl(false);
      })
      .catch(() => setFailedToFetchPreviewUrl(true));
  }, [titleId, previewedFile]);

  const handleClosePreviewButtonClick = () => {
    dispatch(setInventoryModalPreviewedFile(null));
  };

  return (
    <Wrapper>
      <VerticalDivider />
      <Tooltip title="Close preview">
        <IconButtonStyles onClick={handleClosePreviewButtonClick}>
          <PolarisIcon icon="window_collapse_right" />
        </IconButtonStyles>
      </Tooltip>
      <Divider sx={{ mt: 1, mb: 2 }} />
      <PreviewPanelHeader>Preview</PreviewPanelHeader>
      <InventoryPreviewWrapper>
        {failedToFetchPreviewUrl ? (
          <Alert severity="warning" sx={{ pl: 0, pr: 0 }}>
            Failed to load preview
          </Alert>
        ) : !previewUrl ? (
          <CircularProgress sx={{ mx: 'auto', my: 5, display: 'block' }} />
        ) : (
          <InventoryPreview fileType={previewedFile.file_type} source={previewUrl} />
        )}
      </InventoryPreviewWrapper>
      <PreviewPanelFileName>{previewedFile.name}</PreviewPanelFileName>
    </Wrapper>
  );
};

export default PreviewPanel;

const Wrapper = styled.div`
  position: relative;
  flex-basis: 30%;
  min-width: 354px;
  padding: ${({ theme }) => theme.spacing(1, 3, 0)};
`;

const getDividerRGBAColor = (opacity: number) => `rgba(67, 77, 150, ${opacity})`;

const VerticalDivider = styled.div`
  position: absolute;
  top: 0;
  bottom: 20px;
  left: 0;
  width: 1px;
  background: linear-gradient(to bottom, ${getDividerRGBAColor(0)} 0%, ${getDividerRGBAColor(1)} 20%);
`;

const PreviewPanelHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  font-size: 0.92rem;
`;

const IconButtonStyles = styled(IconButton)`
  margin-left: ${({ theme }) => theme.spacing(-1.25)};
  opacity: 0.7;
  transition: ease opacity 250ms;

  &:hover {
    opacity: 1;
  }
`;

const InventoryPreviewWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const PreviewPanelFileName = styled.div`
  word-break: break-all;
  font-size: 0.92rem;
  font-weight: 700;
`;
